import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import { Box, styled } from '@mui/material';

import {
  TransportationAttribute,
  TransportationAttributeGroup,
  TransportationAttributeSubgrouping,
} from '@/data/models/transportation-attribute.model';
import {
  AttributeControlType,
  SecondaryAttribute,
} from '../schema/secondaryAttributes.model';

import {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';

import Checkbox from '@/components/forms/Checkbox';
import SelectMenu from '@/components/forms/SelectMenu';

const PREFIX = 'AdditionalPassengers';

const classes = {
  subctrl: `${PREFIX}-subctrl`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),

  [`& .${classes.subctrl}`]: {
    marginLeft: theme.spacing(4),
  },
}));

type AdditionalPassengersProps = {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  transportationAttributes: TransportationAttribute[];
};

const AdditionalPassengers = ({
  form,
  transportationAttributes,
}: AdditionalPassengersProps) => {
  const { t } = useTranslation('common');
  const additionalPassengers = form.watch('AdditionalPassengers');

  const emptyAttribute: TransportationAttribute = useMemo(
    () => ({
      transportationAttributeCategoryBC: '',
      name: t('reservation.noAttribute'),
      description: '',
      isPassengerSelectable: true,
      isDefault: false,
    }),
    [t],
  );

  const additionalPassengerChoices = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
  ];

  const passengerAttributes = useMemo(() => {
    if (!transportationAttributes) return [];

    const passengerAttrs: SecondaryAttribute[] = [];
    const secondary = transportationAttributes.filter(
      (a) =>
        a.grouping === TransportationAttributeGroup.SECONDARY &&
        a.isPassengerSelectable,
    );
    secondary.forEach((s) => {
      let options: TransportationAttribute[] | undefined = undefined;
      if (s.childTransportationAttributeCategoryBC) {
        const children = transportationAttributes.filter(
          (ta) =>
            ta.transportationAttributeCategoryBC ===
            s.childTransportationAttributeCategoryBC,
        );
        if (children?.length) {
          options = children.sort((c1, c2) => c1.name.localeCompare(c2.name));
        }
      }
      const sAttr: SecondaryAttribute = {
        id: s.transportationAttributeUUID!,
        name: s.name,
        controlType: options
          ? AttributeControlType.DROPDOWN
          : AttributeControlType.CHECKBOX,
        listOptions: options,
        displayOrder: s.displayOrder || 0,
      };
      if (
        s.subgrouping === TransportationAttributeSubgrouping.ADDITIONALPASSENGER
      ) {
        passengerAttrs.push(sAttr);
      }
    });
    passengerAttrs.sort((a, b) => a.displayOrder - b.displayOrder);
    return passengerAttrs;
  }, [transportationAttributes]);

  return (
    <StyledBox>
      <Checkbox
        name={`AdditionalPassengers`}
        label={t('reservation.additionalPassengers')}
        control={form.control as any}
      />
      {additionalPassengers && (
        <>
          <SelectMenu
            className={classes.subctrl}
            label={t('reservation.additionalPassengerCount')}
            name={`AdditionalPassengerCount`}
            values={additionalPassengerChoices}
            labelAttr="label"
            valueAttr="value"
            required={true}
            control={form.control as any}
            errorMessage={
              form.formState.errors.AdditionalPassengerCount?.message as string
            }
          />
          {!!passengerAttributes.length &&
            passengerAttributes.map((sa) => {
              if (sa.controlType === AttributeControlType.CHECKBOX) {
                return (
                  <Checkbox
                    key={sa.id}
                    className={classes.subctrl}
                    name={`SecondaryAttribute_${sa.id}`}
                    label={sa.name}
                    control={form.control as any}
                  />
                );
              } else if (
                sa.controlType === AttributeControlType.DROPDOWN &&
                sa.listOptions?.length
              ) {
                return (
                  <SelectMenu
                    key={sa.id}
                    className={classes.subctrl}
                    label={sa.name}
                    name={`SecondaryAttribute_${sa.id}`}
                    values={[emptyAttribute, ...sa.listOptions]}
                    valueAttr="transportationAttributeUUID"
                    labelAttr="name"
                    control={form.control as any}
                  />
                );
              }
            })}
        </>
      )}
    </StyledBox>
  );
};

export { AdditionalPassengers };
